import React, { useState } from 'react'
import { Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import SearchInput from '@/components/search-input/search-input'
import $msg from '@/utils/message'
import { layerManage, showLayer } from '@/utils/layer'
import { getMemberLevelChangeRecords } from '../api'
import MemberRecordDetail from '../member-record-detail/member-record-detail'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

const changeTypeMap: Record<number, string> = {
    0: '自动升级',
    1: '自动降级',
    2: '手动调整',
}

const getOperatorName = (operator?: any) =>
    operator?.name ||
    operator?.realname ||
    operator?.nickname ||
    operator?.user?.realname ||
    operator?.user?.nickname ||
    '系统'

const MemberLevelChangeRecord: React.FC = () => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [keyword, setKeyword] = useState('')
    const [searchKey, setSearchKey] = useState(0)
    const req = useRequest(
        () => getMemberLevelChangeRecords({ page, limit: pageSize, keyword: keyword || undefined }),
        {
            refreshDeps: [page, pageSize, keyword],
            onError: e => $msg('error', e.message || '获取等级变更记录失败'),
        },
    )
    const showRecordDetail = (record: any) => {
        showLayer(
            <MemberRecordDetail
                title="等级变更详情"
                onClose={() => layerManage.pop()}
                sections={[
                    {
                        title: '会员信息',
                        items: [
                            {
                                label: '会员',
                                value: record.membership?.name || record.membership?.user?.nickname || '--',
                            },
                            { label: '手机号', value: record.membership?.phone || '--' },
                        ],
                    },
                    {
                        title: '变更信息',
                        items: [
                            {
                                label: '变更时间',
                                value: record.create_time
                                    ? dayjs(record.create_time).format('YYYY-MM-DD HH:mm:ss')
                                    : '--',
                            },
                            { label: '变更类型', value: changeTypeMap[record.change_type] || '--' },
                            { label: '变更前等级', value: record.from_level?.name || '--' },
                            { label: '变更后等级', value: record.to_level?.name || '--' },
                            { label: '操作人', value: getOperatorName(record.operator) },
                        ],
                    },
                ]}
            />,
        )
    }
    const columns: ColumnsType<any> = [
        {
            title: '变更时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        {
            title: '会员',
            dataIndex: 'membership',
            width: 180,
            render: value => value?.name || value?.user?.nickname || '--',
        },
        {
            title: '手机号',
            dataIndex: ['membership', 'phone'],
            width: 150,
            render: value => value || '--',
        },
        { title: '变更前等级', dataIndex: ['from_level', 'name'], width: 150, render: value => value || '--' },
        { title: '变更后等级', dataIndex: ['to_level', 'name'], width: 150, render: value => value || '--' },
        {
            title: '变更类型',
            dataIndex: 'change_type',
            width: 120,
            render: value => changeTypeMap[value] || '--',
        },
        {
            title: '操作人',
            dataIndex: 'operator',
            width: 140,
            render: getOperatorName,
        },
        {
            title: '操作',
            width: 88,
            fixed: 'right',
            render: (_value, record) => (
                <span className="action" onClick={() => showRecordDetail(record)}>
                    详情
                </span>
            ),
        },
    ]

    return (
        <SettingPage
            title="等级变更记录"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <>
                    <div className="member-basic-setting__search">
                        <SearchInput
                            key={searchKey}
                            placeholder="输入会员姓名或手机号"
                            onSearch={value => {
                                setPage(1)
                                setKeyword(value)
                            }}
                            onDelete={() => {
                                setPage(1)
                                setKeyword('')
                            }}
                        />
                    </div>
                    <Button
                        types="primary"
                        onClick={() => {
                            setPage(1)
                            setKeyword('')
                            setSearchKey(value => value + 1)
                        }}
                    >
                        重置
                    </Button>
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                </>
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => record.id || `${record.create_time}-${index}`}
                    loading={req.loading}
                    columns={columns}
                    dataSource={req.data?.change_records || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default MemberLevelChangeRecord
